import { createSignal, For, Show } from "solid-js";
import { createQuery } from "@tanstack/solid-query";
import { useConfirm } from "../shared/confirm-dialog";
import PolicyEditor, { type Preset } from "../panels/policy-editor";
import type { PolicyObject } from "../shared/host-policy-schema";

interface Tenant {
  id: string;
  name: string;
  slug: string;
  policy?: PolicyObject;
  quotas: {
    maxSessions: number | null;
    maxOperators: number | null;
    maxHosts: number | null;
    maxPlugins: number | null;
  };
  usage: { operators: number; hosts: number; clients: number };
  createdAt: string;
}

interface AdminUser {
  id: string;
  email: string | null;
  name: string | null;
  roles: string[];
  tenantId: string | null;
  disabledAt: string | null;
}

async function jget<T>(url: string): Promise<T> {
  const r = await fetch(url, { credentials: "include" });
  if (!r.ok) throw new Error(`${url} → ${r.status}`);
  return r.json() as Promise<T>;
}

async function jsend(url: string, method: string, body?: unknown): Promise<Record<string, unknown>> {
  const r = await fetch(url, {
    method,
    credentials: "include",
    headers: { "content-type": "application/json" },
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });
  const data = (await r.json().catch(() => ({}))) as Record<string, unknown>;
  if (!r.ok) throw new Error((data?.error as string) || `HTTP ${r.status}`);
  return data;
}

function errorMessage(error: unknown): string | null {
  if (!error) return null;
  return error instanceof Error ? error.message : String(error);
}

export function SuperAdminTenantsPage() {
  const { confirm } = useConfirm();
  const [err, setErr] = createSignal<string | null>(null);
  const [newName, setNewName] = createSignal("");
  const [newSlug, setNewSlug] = createSignal("");
  const [newMaxSessions, setNewMaxSessions] = createSignal("");
  const [newMaxOperators, setNewMaxOperators] = createSignal("");
  const [newMaxHosts, setNewMaxHosts] = createSignal("");
  const [newMaxPlugins, setNewMaxPlugins] = createSignal("");
  const [adminTenantId, setAdminTenantId] = createSignal<string | null>(null);
  const [adminName, setAdminName] = createSignal("");
  const [adminEmail, setAdminEmail] = createSignal("");
  const [adminPassword, setAdminPassword] = createSignal("");

  // État d'édition inline (PATCH /api/super-admin/tenants/:id)
  const [editingId, setEditingId] = createSignal<string | null>(null);
  const [editName, setEditName] = createSignal("");
  const [editSlug, setEditSlug] = createSignal("");
  const [editMaxSessions, setEditMaxSessions] = createSignal("");
  const [editMaxOperators, setEditMaxOperators] = createSignal("");
  const [editMaxHosts, setEditMaxHosts] = createSignal("");
  const [editMaxPlugins, setEditMaxPlugins] = createSignal("");

  // ── Éditeur de stratégies (policy racine du tenant) ──
  // Le tenant est la racine de la hiérarchie tenant → client → site → host.
  // Sa policy s'applique à tous les hosts du tenant sauf override explicite.
  const presetsQ = createQuery<Preset[]>(() => ({
    queryKey: ["host-policy-presets"],
    queryFn: () => jget<{ presets: Preset[] }>("/api/host-policy/presets").then((d) => d.presets || []),
  }));
  const [tenantPolicyModal, setTenantPolicyModal] = createSignal<{
    tenantId: string;
    label: string;
    initial: PolicyObject;
  } | null>(null);

  function openTenantPolicyEditor(t: Tenant) {
    setTenantPolicyModal({
      tenantId: t.id,
      label: t.name,
      initial: t.policy || {},
    });
  }
  async function saveTenantPolicy(patch: PolicyObject) {
    const m = tenantPolicyModal();
    if (!m) return;
    setErr(null);
    try {
      await jsend(`/api/super-admin/tenants/${m.tenantId}`, "PATCH", { policy: patch });
      setTenantPolicyModal(null);
      await tenantsQ.refetch();
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    }
  }
  function hasCustomTenantPolicy(p?: PolicyObject): boolean {
    return !!p && Object.keys(p).length > 0;
  }

  const tenantsQ = createQuery<{ ok: boolean; tenants: Tenant[] }>(() => ({
    queryKey: ["super-admin-tenants"],
    queryFn: () => jget("/api/super-admin/tenants"),
  }));

  const adminsQ = createQuery<{ users: AdminUser[] }>(() => ({
    queryKey: ["super-admin-tenant-admins"],
    queryFn: () => jget("/auth/admin/users?role=admin&disabled=false"),
  }));

  function adminsForTenant(tenantId: string) {
    return (adminsQ.data?.users || []).filter((u) => u.tenantId === tenantId && u.roles.includes("admin"));
  }

  const loadError = () => errorMessage(tenantsQ.error) || errorMessage(adminsQ.error);

  function startEdit(t: Tenant) {
    setEditingId(t.id);
    setEditName(t.name);
    setEditSlug(t.slug);
    setEditMaxSessions(t.quotas.maxSessions != null ? String(t.quotas.maxSessions) : "");
    setEditMaxOperators(t.quotas.maxOperators != null ? String(t.quotas.maxOperators) : "");
    setEditMaxHosts(t.quotas.maxHosts != null ? String(t.quotas.maxHosts) : "");
    setEditMaxPlugins(t.quotas.maxPlugins != null ? String(t.quotas.maxPlugins) : "");
  }

  function cancelEdit() {
    setEditingId(null);
  }

  function saveEdit(id: string) {
    const name = editName().trim();
    if (!name) return;
    const numOrNull = (v: string) => (v.trim() === "" ? null : Number(v));
    run(() =>
      jsend(`/api/super-admin/tenants/${id}`, "PATCH", {
        name,
        slug: editSlug().trim() || undefined,
        maxSessions: numOrNull(editMaxSessions()),
        maxOperators: numOrNull(editMaxOperators()),
        maxHosts: numOrNull(editMaxHosts()),
        maxPlugins: numOrNull(editMaxPlugins()),
      }),
    ).then((ok) => {
      if (ok) setEditingId(null);
    });
  }

  async function run(fn: () => Promise<unknown>): Promise<boolean> {
    setErr(null);
    try {
      await fn();
      tenantsQ.refetch();
      return true;
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
      return false;
    }
  }

  function createTenant() {
    const name = newName().trim();
    if (!name) return;
    run(() =>
      jsend("/api/super-admin/tenants", "POST", {
        name,
        slug: newSlug().trim() || undefined,
        maxSessions: newMaxSessions() ? Number(newMaxSessions()) : null,
        maxOperators: newMaxOperators() ? Number(newMaxOperators()) : null,
        maxHosts: newMaxHosts() ? Number(newMaxHosts()) : null,
        maxPlugins: newMaxPlugins() ? Number(newMaxPlugins()) : null,
      }),
    ).then((ok) => {
      if (!ok) return;
      setNewName("");
      setNewSlug("");
      setNewMaxSessions("");
      setNewMaxOperators("");
      setNewMaxHosts("");
      setNewMaxPlugins("");
    });
  }

  async function createTenantAdmin(tenantId: string) {
    const name = adminName().trim();
    const email = adminEmail().trim().toLowerCase();
    const password = adminPassword();
    if (!name || !email || password.length < 8) {
      setErr("Nom, email et mot de passe de 8 caractères minimum requis.");
      return;
    }
    setErr(null);
    try {
      await jsend("/auth/admin/users", "POST", {
        name,
        email,
        password,
        roles: ["admin", "support"],
        tenantId,
        mustChangePassword: true,
      });
      setAdminTenantId(null);
      setAdminName("");
      setAdminEmail("");
      setAdminPassword("");
      adminsQ.refetch();
      tenantsQ.refetch();
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    }
  }

  async function deleteTenant(id: string, name: string) {
    const ok = await confirm({
      level: "danger",
      title: "Supprimer ce tenant ?",
      body: "Les données associées (hosts, sessions, opérateurs) seront supprimées.",
      entityName: name,
    });
    if (!ok) return;
    run(() => jsend(`/api/super-admin/tenants/${id}`, "DELETE"));
  }

  const inputStyle = {
    background: "var(--bg-2, #1e293b)",
    color: "var(--text-1, #e8ecf3)",
    border: "1px solid var(--border, #334155)",
    "border-radius": "4px",
    padding: "4px 8px",
    "font-size": "12px",
  } as const;

  return (
    <div class="plugins-console" style={{ overflow: "auto" }}>
      <div class="plugins-console-header">
        <span class="plugins-console-title">
          Tenants
          <span class="plugins-console-sub">gestion multi-tenant + quotas</span>
        </span>
      </div>

      <Show when={err() || loadError()}>
        <div
          style={{
            background: "rgba(220,38,38,0.12)",
            border: "1px solid var(--danger, #dc2626)",
            color: "var(--danger, #f87171)",
            padding: "6px 10px",
            "border-radius": "4px",
            "font-size": "12px",
            "margin-bottom": "10px",
          }}
        >
          {err() || loadError()}
        </div>
      </Show>

      <div class="rmm-table-wrap" style={{ "margin-bottom": "14px" }}>
        <div style={{ display: "flex", gap: "6px", padding: "8px", "flex-wrap": "wrap", "align-items": "center" }}>
          <input
            style={inputStyle}
            placeholder="Nom du tenant"
            value={newName()}
            onInput={(e) => setNewName(e.currentTarget.value)}
          />
          <input
            style={inputStyle}
            placeholder="Slug (optionnel)"
            value={newSlug()}
            onInput={(e) => setNewSlug(e.currentTarget.value)}
          />
          <input
            style={{ ...inputStyle, width: "70px" }}
            type="number"
            placeholder="Sessions max"
            value={newMaxSessions()}
            onInput={(e) => setNewMaxSessions(e.currentTarget.value)}
          />
          <input
            style={{ ...inputStyle, width: "70px" }}
            type="number"
            placeholder="Opérateurs max"
            value={newMaxOperators()}
            onInput={(e) => setNewMaxOperators(e.currentTarget.value)}
          />
          <input
            style={{ ...inputStyle, width: "70px" }}
            type="number"
            placeholder="Hosts max"
            value={newMaxHosts()}
            onInput={(e) => setNewMaxHosts(e.currentTarget.value)}
          />
          <input
            style={{ ...inputStyle, width: "70px" }}
            type="number"
            placeholder="Plugins max"
            value={newMaxPlugins()}
            onInput={(e) => setNewMaxPlugins(e.currentTarget.value)}
          />
          <button class="btn btn--primary btn--xs" type="button" onClick={createTenant}>
            + Créer
          </button>
        </div>

        <table class="rmm-table">
          <thead>
            <tr>
              <th>Tenant</th>
              <th>Slug</th>
              <th>Quotas</th>
              <th>Usage</th>
              <th>Admins</th>
              <th>Créé</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            <Show
              when={!tenantsQ.isLoading}
              fallback={
                <tr>
                  <td colSpan={7}>Chargement…</td>
                </tr>
              }
            >
              <For each={tenantsQ.data?.tenants || []}>
                {(t) => {
                  const tenantAdmins = () => adminsForTenant(t.id);
                  return (
                    <>
                      <Show
                        when={editingId() === t.id}
                        fallback={
                          <tr>
                            <td style={{ color: "var(--text-1)", "font-weight": "500" }}>{t.name}</td>
                            <td
                              style={{
                                "font-family": "var(--font-mono)",
                                "font-size": "11px",
                                color: "var(--text-4)",
                              }}
                            >
                              {t.slug}
                            </td>
                            <td style={{ "font-size": "11px", color: "var(--text-3)" }}>
                              S:{t.quotas.maxSessions ?? "∞"} O:{t.quotas.maxOperators ?? "∞"} H:
                              {t.quotas.maxHosts ?? "∞"} P:{t.quotas.maxPlugins ?? "∞"}
                            </td>
                            <td style={{ "font-size": "11px", color: "var(--text-3)" }}>
                              Op:{t.usage.operators} H:{t.usage.hosts} C:{t.usage.clients}
                            </td>
                            <td style={{ "font-size": "11px", color: "var(--text-3)" }}>
                              <Show when={tenantAdmins().length > 0} fallback="—">
                                <For each={tenantAdmins()}>
                                  {(u) => (
                                    <div title={u.email || ""}>
                                      {u.name || u.email}
                                      {u.disabledAt ? " (désactivé)" : ""}
                                    </div>
                                  )}
                                </For>
                              </Show>
                            </td>
                            <td style={{ "font-size": "11px", color: "var(--text-4)" }}>
                              {new Date(t.createdAt).toLocaleDateString()}
                            </td>
                            <td style={{ display: "flex", gap: "4px", "flex-wrap": "wrap" }}>
                              <Show when={hasCustomTenantPolicy(t.policy)}>
                                <span class="tag" title="Stratégie racine personnalisée">
                                  policy
                                </span>
                              </Show>
                              <button class="btn btn--xs" type="button" onClick={() => startEdit(t)}>
                                Éditer
                              </button>
                              <button
                                class="btn btn--xs"
                                type="button"
                                onClick={() => openTenantPolicyEditor(t)}
                                title="Stratégie racine du tenant (s'applique à tous ses hosts sauf override)"
                              >
                                Stratégie
                              </button>
                              <button
                                class="btn btn--xs"
                                type="button"
                                onClick={() => {
                                  setAdminTenantId(adminTenantId() === t.id ? null : t.id);
                                  setAdminName("");
                                  setAdminEmail("");
                                  setAdminPassword("");
                                }}
                              >
                                Admin
                              </button>
                              <button
                                class="btn btn--xs btn--danger"
                                type="button"
                                onClick={() => deleteTenant(t.id, t.name)}
                              >
                                Supprimer
                              </button>
                            </td>
                          </tr>
                        }
                      >
                        <tr>
                          <td>
                            <input
                              style={{ ...inputStyle, width: "120px" }}
                              value={editName()}
                              onInput={(e) => setEditName(e.currentTarget.value)}
                            />
                          </td>
                          <td>
                            <input
                              style={{ ...inputStyle, width: "100px" }}
                              value={editSlug()}
                              onInput={(e) => setEditSlug(e.currentTarget.value)}
                            />
                          </td>
                          <td>
                            <div style={{ display: "flex", gap: "3px" }}>
                              <input
                                style={{ ...inputStyle, width: "46px" }}
                                type="number"
                                title="Sessions max"
                                value={editMaxSessions()}
                                onInput={(e) => setEditMaxSessions(e.currentTarget.value)}
                              />
                              <input
                                style={{ ...inputStyle, width: "46px" }}
                                type="number"
                                title="Opérateurs max"
                                value={editMaxOperators()}
                                onInput={(e) => setEditMaxOperators(e.currentTarget.value)}
                              />
                              <input
                                style={{ ...inputStyle, width: "46px" }}
                                type="number"
                                title="Hosts max"
                                value={editMaxHosts()}
                                onInput={(e) => setEditMaxHosts(e.currentTarget.value)}
                              />
                              <input
                                style={{ ...inputStyle, width: "46px" }}
                                type="number"
                                title="Plugins max"
                                value={editMaxPlugins()}
                                onInput={(e) => setEditMaxPlugins(e.currentTarget.value)}
                              />
                            </div>
                          </td>
                          <td style={{ "font-size": "11px", color: "var(--text-3)" }}>
                            Op:{t.usage.operators} H:{t.usage.hosts} C:{t.usage.clients}
                          </td>
                          <td style={{ "font-size": "11px", color: "var(--text-3)" }}>{tenantAdmins().length}</td>
                          <td style={{ "font-size": "11px", color: "var(--text-4)" }}>
                            {new Date(t.createdAt).toLocaleDateString()}
                          </td>
                          <td style={{ display: "flex", gap: "4px" }}>
                            <button class="btn btn--xs btn--primary" type="button" onClick={() => saveEdit(t.id)}>
                              Enregistrer
                            </button>
                            <button class="btn btn--xs" type="button" onClick={cancelEdit}>
                              Annuler
                            </button>
                          </td>
                        </tr>
                      </Show>
                      <Show when={adminTenantId() === t.id}>
                        <tr>
                          <td colSpan={7} style={{ background: "var(--bg-2, #1e293b)", padding: "8px" }}>
                            <div
                              style={{
                                display: "flex",
                                gap: "6px",
                                "flex-wrap": "wrap",
                                "align-items": "center",
                              }}
                            >
                              <span style={{ "font-size": "12px", color: "var(--text-3)" }}>
                                Nouvel admin pour {t.name}
                              </span>
                              <input
                                style={inputStyle}
                                placeholder="Nom"
                                value={adminName()}
                                onInput={(e) => setAdminName(e.currentTarget.value)}
                              />
                              <input
                                style={inputStyle}
                                type="email"
                                placeholder="email@tenant.fr"
                                value={adminEmail()}
                                onInput={(e) => setAdminEmail(e.currentTarget.value)}
                              />
                              <input
                                style={inputStyle}
                                type="password"
                                placeholder="Mot de passe temporaire"
                                value={adminPassword()}
                                onInput={(e) => setAdminPassword(e.currentTarget.value)}
                              />
                              <button
                                class="btn btn--primary btn--xs"
                                type="button"
                                onClick={() => createTenantAdmin(t.id)}
                              >
                                Créer admin tenant
                              </button>
                            </div>
                          </td>
                        </tr>
                      </Show>
                    </>
                  );
                }}
              </For>
              <Show when={!tenantsQ.error && (tenantsQ.data?.tenants || []).length === 0}>
                <tr>
                  <td colSpan={7} style={{ color: "var(--text-4)", "text-align": "center", padding: "14px" }}>
                    Aucun tenant.
                  </td>
                </tr>
              </Show>
            </Show>
          </tbody>
        </table>
      </div>

      {/* ── Modale éditeur de stratégie (tenant racine) ───────────────── */}
      <Show when={tenantPolicyModal()}>
        {(m) => (
          <div
            style={{
              position: "fixed",
              inset: "0",
              background: "rgba(0,0,0,0.55)",
              "z-index": "999",
              display: "flex",
              "align-items": "flex-start",
              "justify-content": "center",
              overflow: "auto",
              padding: "24px 12px",
            }}
            onClick={() => setTenantPolicyModal(null)}
          >
            <div
              style={{
                background: "var(--bg-1, #131517)",
                border: "1px solid var(--border, #2a2e34)",
                "border-radius": "8px",
                width: "min(820px, 96vw)",
                "max-height": "92vh",
                overflow: "auto",
                padding: "16px",
                "box-shadow": "0 8px 32px rgba(0,0,0,0.4)",
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <PolicyEditor
                scopeType="tenant"
                scopeId={m().tenantId}
                scopeLabel={m().label}
                initialPolicy={m().initial}
                presets={presetsQ.data || []}
                onSave={saveTenantPolicy}
                onCancel={() => setTenantPolicyModal(null)}
              />
            </div>
          </div>
        )}
      </Show>
    </div>
  );
}
